// =============================================================================
// SIREPPAR - Modal de opciones: un botón que abre varias salidas
// =============================================================================
// Cuando una etapa tiene varias resoluciones posibles y ninguna es "la normal",
// tres botones sueltos en el encabezado del expediente compiten entre sí y hacen
// pensar que hay tres tareas pendientes. Es el caso del expediente que está en el
// Tribunal: hay UNA tarea —registrar lo que el TEP resolvió— con tres resultados
// posibles (aprueba / rechaza / aplaza).
//
// Este modal es el paso intermedio: un botón único abre la lista, se elige el
// resultado y recién ahí se abre el modal de la resolución. Cada opción explica su
// consecuencia, que es lo que un botón en una barra no puede decir.
// =============================================================================

import Modal from './Modal';

export interface OpcionAccion {
  key: string;
  label: string;
  /** Qué pasa si se elige. Se muestra debajo del label. */
  descripcion: string;
  tono: 'ok' | 'bad' | 'warn';
}

interface Props {
  abierto: boolean;
  titulo: string;
  ayuda?: string;
  opciones: OpcionAccion[];
  onElegir: (key: string) => void;
  onClose: () => void;
}

const TONOS: Record<OpcionAccion['tono'], string> = {
  ok: 'border-green-200 hover:border-green-400 hover:bg-green-50',
  bad: 'border-red-200 hover:border-red-400 hover:bg-red-50',
  warn: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
};
const PUNTOS: Record<OpcionAccion['tono'], string> = {
  ok: 'bg-green-500',
  bad: 'bg-red-500',
  warn: 'bg-amber-500',
};

export default function ModalOpcionesAccion({
  abierto, titulo, ayuda, opciones, onElegir, onClose,
}: Props) {
  return (
    <Modal isOpen={abierto} onClose={onClose} title={titulo} size="md">
      <div className="space-y-3">
        {ayuda && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">{ayuda}</p>}
        {opciones.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => onElegir(o.key)}
            className={`w-full text-left border rounded-lg p-4 transition flex items-start gap-3 ${TONOS[o.tono]}`}
          >
            <span className={`mt-1.5 h-2.5 w-2.5 rounded-full flex-shrink-0 ${PUNTOS[o.tono]}`} />
            <span>
              <span className="block font-medium text-gray-900">{o.label}</span>
              <span className="block text-sm text-gray-500 mt-0.5">{o.descripcion}</span>
            </span>
          </button>
        ))}
        <div className="flex justify-end pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}
